import { DATE_FORMATS, HTMLTagRegEx } from "../../../constants";
import {
  beautifyDurationUnit,
  calculateFinancialSupport,
  calculateIntakeName,
  toCurrency,
} from "../../../helpers";
import {
  programDetailsConfig,
  HOUSING_TYPES_MAP,
  PROGRAM_TYPES,
} from "../constants";
import { mergeProgramSteps } from "./parseToProgramBackEndModel";
import { calcFeeWithScholarshipAndFinancialAid } from "./helperFunctions";

function getBranchesNames(branches, branchIds) {
  return branchIds.map(
    (b) => branches.find((branch) => branch.id === b)?.name || "",
  );
}

export function parseToCreateProgramPreview({ programRawData, branches }) {
  const program = mergeProgramSteps(programRawData);

  const programPreviewData = {
    intakes: [],
    exams: [],
    details: [],
    detailFiles: [],
    branches: [],
    otherFees: [],
  } as any;

  // Major Id  and Name-------------------------------------
  if (program.major) {
    programPreviewData.majorId = program.major.id;
    programPreviewData.majorName = program.major.name;
  }

  // Program Type -------------------------------------------
  programPreviewData.programType = PROGRAM_TYPES.find(
    (type) => type.id === +program.programType,
  )!.name;

  // Housing Type ---------------------------------------------
  if (program.housingTypes) {
    programPreviewData.housingTypes = program.housingTypes.map(
      (type) => HOUSING_TYPES_MAP[type],
    );
  }

  // Intakes --------------------------------------------------
  [
    ...(program.existingIntakes || []),
    ...(program.defaultNewIntakes || []),
    ...(program.newIntakes || []),
  ].forEach((intake) => {
    if (intake && intake.deadline && intake.startDate) {
      programPreviewData.intakes.push({
        deadline: intake.deadline.format(DATE_FORMATS.frontEnd),
        startDate: intake.startDate.format(DATE_FORMATS.frontEnd),
        intake: calculateIntakeName(
          intake.startDate.format(DATE_FORMATS.frontEnd),
        ),
      });
    }
  });

  //  Branches ---------------------------------------------
  programPreviewData.branches = getBranchesNames(branches, program.branches);

  //  Exams ------------------------------------------------
  [...(program.mainExams || []), ...(program.addedExams || [])].forEach(
    (exam) => {
      if (exam?.name) {
        programPreviewData.exams.push({
          name: exam.name,
          score: exam.score,
        });
      }
    },
  );

  // Program Duration -------------------------------------------------
  programPreviewData.durationAmount = program.durationAmount;
  programPreviewData.durationUnit = beautifyDurationUnit(
    program.durationAmount,
    program.durationUnit.id,
    "Program",
  );

  // Tuition Duration -------------------------------------------------
  programPreviewData.tuitionDurationAmount = program.tuitionDurationAmount
    ? program.tuitionDurationAmount
    : "";
  programPreviewData.tuitionDurationUnit = beautifyDurationUnit(
    program.tuitionDurationAmount,
    program.tuitionDurationUnit.id,
    "Program",
  );

  // Finance --------------------------------------------------
  const currency = +program.currency.id;
  const tuition = +program.tuition;

  const {
    feeWithCurrency: tuitionWithCurrency,
    calcFeeWithCurrency: calcTuitionWithCurrency,
    scholarshipWithCurrency: tuitionScholarshipWithCurrency,
    financialAidWithCurrency: tuitionFinancialAidWithCurrency,
    hasSupport: hasTuitionSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    tuition,
    program.hasTuitionScholarship,
    program.tuitionScholarshipType?.id,
    program.tuitionScholarshipAmount,
    program.hasTuitionFinancialAid,
    program.tuitionFinancialAidType?.id,
    program.tuitionFinancialAidAmount,
    currency,
  );

  // Tuition --------------------------------------------------
  programPreviewData.tuition = tuitionWithCurrency;
  // Commission
  const commissionAmount = calculateFinancialSupport(
    tuition,
    true,
    program.commissionType.id,
    program.commissionAmount,
  );
  programPreviewData.commissionAmount = toCurrency(
    commissionAmount,
    currency,
    " ",
    "code",
  );
  // Accommodation Fees
  if (program.boardingAmount) {
    programPreviewData.boardingAmount = toCurrency(
      program.boardingAmount,
      currency,
      " ",
      "code",
    );
  }

  if (program.homestayAmount) {
    programPreviewData.homestayAmount = toCurrency(
      program.homestayAmount,
      currency,
      " ",
      "code",
    );
  }

  if (program.insuranceAmount) {
    programPreviewData.insuranceAmount = toCurrency(
      program.insuranceAmount,
      currency,
      " ",
      "code",
    );
  }

  programPreviewData.tuitionScholarship = tuitionScholarshipWithCurrency;

  programPreviewData.tuitionFinancialAid = tuitionFinancialAidWithCurrency;

  programPreviewData.calculatedTuition = calcTuitionWithCurrency;

  // Other Fees -----------------------------------------------
  let otherFeesSum = 0;
  [...(program.mainOtherFees || []), ...(program.addedOtherFees || [])].forEach(
    (fee) => {
      if (fee?.name && fee?.amount) {
        programPreviewData.otherFees.push({
          name: fee.name,
          amount: toCurrency(fee.amount, currency, " ", "code"),
        });

        otherFeesSum += +fee.amount;
      }
    },
  );

  const {
    feeWithCurrency: otherFeesWithCurrency,
    calcFeeWithCurrency: calcOtherFeesWithCurrency,
    scholarshipWithCurrency: otherFeesScholarshipWithCurrency,
    financialAidWithCurrency: otherFeesFinancialAidWithCurrency,
    hasSupport: hasOtherFeesSupport,
  } = calcFeeWithScholarshipAndFinancialAid(
    otherFeesSum,
    program.hasOtherFeesScholarship,
    program.otherFeesScholarshipType?.id,
    program.otherFeesScholarshipAmount,
    program.hasOtherFeesFinancialAid,
    program.otherFeesFinancialAidType?.id,
    program.otherFeesFinancialAidAmount,
    currency,
  );

  programPreviewData.otherFeesSum = otherFeesWithCurrency;

  programPreviewData.otherFeesScholarship = otherFeesScholarshipWithCurrency;

  programPreviewData.otherFeesFinancialAid = otherFeesFinancialAidWithCurrency;

  programPreviewData.calculatedOtherFees = calcOtherFeesWithCurrency;

  programPreviewData.hasTuitionSupport = hasTuitionSupport;
  programPreviewData.hasOtherFeesSupport = hasOtherFeesSupport;

  //Details
  programDetailsConfig.forEach((item) => {
    if (
      program.details[`type_${item.type}`] &&
      (program.details[`type_${item.type}`] as string)
        .replace(HTMLTagRegEx, "")
        .trim().length
    ) {
      programPreviewData.details.push({
        type: item.type,
        typeName: item.name,
        content: program.details[`type_${item.type}`],
      });
    }
  });

  //Detail Files
  (program.detailFiles || []).map((file) =>
    programPreviewData.detailFiles.push({
      id: file.id,
      name: file.name!,
      size: file.size!,
      path: file.preview!,
    }),
  );

  return programPreviewData;
}
